import React from "react";
import {
  Box, Card, CardContent, Grid, Typography, useTheme, useMediaQuery,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  AccountBalance as AccountBalanceIcon,
  ShowChart as ShowChartIcon,
} from "@mui/icons-material";
import Utils from "../util/Utils";

// ── Design tokens (match CompanyProfile) ───────────────────────────────────────
const C = {
  textPrimary:    "#0f1f35",
  textSecondary:  "#3d5166",
  textMuted:      "#7a92a8",
  bg:             "#ffffff",
  card:           "#ffffff",
  cardHeader:     "#f5f2ed",
  cardHeaderText: "#1a2744",
  border:         "#d0dcea",
  borderLight:    "#e8eef6",
  navyDark:       "#0f2d52",
  navy:           "#1a4075",
  navyMid:        "#2c5282",
  navyLight:      "#dce8f5",
  success:        "#0a8a7e",
  successBg:      "#e6f9f7",
  danger:         "#b91c1c",
  dangerBg:       "#fde8e8",
};

const MONO = "'DM Mono', 'Fira Mono', 'Courier New', monospace";
const SANS = "'Lato', 'Helvetica Neue', Arial, sans-serif";

// ── Shared page header (match profile page) ────────────────────────────────────
function PageHeader({ section, title, subtitle, right }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "flex-start",
      justifyContent: "space-between", flexWrap: "wrap",
      gap: 1, pb: 1.25, mb: 2,
      borderBottom: `1px solid ${C.borderLight}`,
    }}>
      <Box sx={{ borderLeft: `4px solid ${C.navy}`, pl: 1.5 }}>
        <Typography sx={{
          fontSize: { xs: "1.5rem", md: "1.75rem" },
          fontWeight: 700,
          color: C.cardHeaderText, letterSpacing: "-0.01em",
          fontFamily: SANS, lineHeight: 1.2,
        }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{
            mt: 0.35, fontSize: "0.9rem",
            color: C.textSecondary, fontFamily: SANS,
          }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {right && <Box sx={{ flexShrink: 0 }}>{right}</Box>}
    </Box>
  );
}

// ── Section card (match profile TableCard header style) ─────────────────────────
function SectionCard({ title, IconComp, accent, children }) {
  return (
    <Card elevation={0} sx={{
      width: "100%", height: "100%",
      border: `1px solid ${C.border}`,
      borderRadius: "10px",
      boxShadow: "0 1px 3px rgba(15,45,82,0.06)",
      overflow: "hidden",
      transition: "box-shadow 0.15s",
      backgroundColor: C.bg,
      "&:hover": { boxShadow: "0 4px 12px rgba(15,45,82,0.1)" },
      "& .MuiCardContent-root": { p: 1.25, "&:last-child": { pb: 1.25 } },
    }}>
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1.25,
        px: 1.5, py: 1,
        borderBottom: `1px solid ${C.borderLight}`,
        backgroundColor: C.cardHeader,
      }}>
        {IconComp && (
          <IconComp sx={{ fontSize: 20, color: C.navy, flexShrink: 0 }} />
        )}
        <Typography sx={{
          fontWeight: 700, fontSize: "1rem",
          color: C.cardHeaderText, fontFamily: SANS,
        }}>
          {title}
        </Typography>
      </Box>
      <CardContent>
        {children}
      </CardContent>
    </Card>
  );
}

// ── Metric row ────────────────────────────────────────────────────────────────
function MetricRow({ label, value, valueColor, large, divider }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "center",
      justifyContent: "space-between", gap: 1.5,
      py: large ? "0.55rem" : "0.4rem",
      borderBottom: divider ? `1px solid ${C.borderLight}` : "none",
      transition: "background 0.1s", borderRadius: "4px",
      "&:hover": { backgroundColor: C.cardHeader },
    }}>
      <Typography sx={{
        fontSize: large ? "0.98rem" : "0.9rem",
        fontWeight: large ? 600 : 400,
        color: C.textSecondary, fontFamily: SANS,
        lineHeight: 1.35, flex: 1,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: large ? "1.15rem" : "1rem",
        fontWeight: 700,
        color: valueColor || C.textPrimary,
        fontFamily: MONO, whiteSpace: "nowrap", flexShrink: 0,
        fontVariantNumeric: "tabular-nums",
      }}>
        {value}
      </Typography>
    </Box>
  );
}

function AnalysisCard({ title, accent, data }) {
  return (
    <SectionCard title={title} IconComp={ShowChartIcon} accent={accent}>
      {data.map((row, i) => (
        <MetricRow
          key={i}
          label={row.label}
          value={row.value}
          valueColor={row.large ? C.success : C.textSecondary}
          large={row.large}
          divider={i < data.length - 1}
        />
      ))}
    </SectionCard>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function IntrinsicValue({ stockValues }) {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const bestFairValue =
    stockValues.stockReport?.intrinsicValue != null &&
    stockValues.stockReport.intrinsicValue > 0
      ? Utils.format_currency_usd(stockValues.stockReport.intrinsicValue)
      : null;

  const analysisCards = [
    {
      title: "10 Year DCF Analysis", accent: C.navyMid,
      data: [
        { label: "PV of Future Cash Flows",        value: Utils.format_currency_long(stockValues.tenDCFiv.pvOfFut) },
        { label: "Intrinsic Value (pre Cash/Debt)", value: Utils.format_currency_long(stockValues.tenDCFiv.intrinsicValueBeforeCashDebt) },
        { label: "Intrinsic Value",                 value: Utils.format_currency_usd(stockValues.tenDCFiv.fnlIntrinVal), large: true },
      ],
    },
    {
      title: "20 Year DCF Analysis", accent: "#4f46e5",
      data: [
        { label: "PV of Future Cash Flows",        value: Utils.format_currency_long(stockValues.twentyDCFiv.pvOfFut) },
        { label: "Intrinsic Value (pre Cash/Debt)", value: Utils.format_currency_long(stockValues.twentyDCFiv.intrinsicValueBeforeCashDebt) },
        { label: "Intrinsic Value",                 value: Utils.format_currency_usd(stockValues.twentyDCFiv.fnlIntrinVal), large: true },
      ],
    },
    {
      title: "10 Year Earnings Analysis", accent: C.success,
      data: [
        { label: "PV of Future Earnings",          value: Utils.format_currency_long(stockValues.tenDEiv.pvOfFut) },
        { label: "Intrinsic Value (pre Cash/Debt)", value: Utils.format_currency_long(stockValues.tenDEiv.intrinsicValueBeforeCashDebt) },
        { label: "Intrinsic Value",                 value: Utils.format_currency_usd(stockValues.tenDEiv.fnlIntrinVal), large: true },
      ],
    },
    {
      title: "20 Year Earnings Analysis", accent: "#b86b00",
      data: [
        { label: "PV of Future Earnings",          value: Utils.format_currency_long(stockValues.twentyDEiv.pvOfFut) },
        { label: "Intrinsic Value (pre Cash/Debt)", value: Utils.format_currency_long(stockValues.twentyDEiv.intrinsicValueBeforeCashDebt) },
        { label: "Intrinsic Value",                 value: Utils.format_currency_usd(stockValues.twentyDEiv.fnlIntrinVal), large: true },
      ],
    },
  ];

  const modelValues = [
    { label: "10 Year Cash Flows",  value: Utils.format_currency_usd(stockValues.tenDCFiv.fnlIntrinVal) },
    { label: "20 Year Cash Flows",  value: Utils.format_currency_usd(stockValues.twentyDCFiv.fnlIntrinVal) },
    ...(stockValues.tenDCF2iv?.fnlIntrinVal > 0
      ? [{ label: "10 Year DCF Model 2", value: Utils.format_currency_usd(stockValues.tenDCF2iv.fnlIntrinVal) }] : []),
    ...(stockValues.twentyDCF2iv?.fnlIntrinVal > 0
      ? [{ label: "20 Year DCF Model 2", value: Utils.format_currency_usd(stockValues.twentyDCF2iv.fnlIntrinVal) }] : []),
    { label: "10 Year Earnings",    value: Utils.format_currency_usd(stockValues.tenDEiv.fnlIntrinVal) },
    { label: "20 Year Earnings",    value: Utils.format_currency_usd(stockValues.twentyDEiv.fnlIntrinVal) },
  ];

  return (
    <Box sx={{
      px: { xs: 1, sm: 1.5, md: 2 },
      py: 2,
      width: "100%",
      maxWidth: 1280,
      margin: "0 auto",
      boxSizing: "border-box",
      fontFamily: SANS,
      backgroundColor: C.bg,
    }}>
      <PageHeader
        title="Intrinsic Value"
        subtitle="DCF & Earnings models · 10 & 20 year projections"
        right={bestFairValue && (
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{
              fontSize: "0.78rem", fontWeight: 700,
              letterSpacing: "0.1em", textTransform: "uppercase",
              color: C.textMuted, fontFamily: SANS, mb: 0.3,
            }}>
              Best Fair Value
            </Typography>
            <Typography sx={{
              fontSize: "2rem", fontWeight: 700,
              color: C.cardHeaderText, fontFamily: MONO,
              letterSpacing: "-0.02em", lineHeight: 1,
            }}>
              {bestFairValue}
            </Typography>
            <Typography sx={{
              fontSize: "0.76rem", color: C.textMuted,
              fontFamily: SANS, mt: 0.3,
            }}>
              Weighted Blend
            </Typography>
          </Box>
        )}
      />

      {/* All model values */}
      <Box sx={{
        backgroundColor: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: "10px",
        p: 1.25,
        mb: 2,
        boxShadow: "0 1px 3px rgba(15,45,82,0.06)",
      }}>
        <Typography sx={{
          fontSize: "0.875rem", fontWeight: 700,
          color: C.cardHeaderText, fontFamily: SANS, mb: 1,
        }}>
          All Model Values
        </Typography>
        <Box sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : `repeat(${Math.min(modelValues.length, 3)}, 1fr)`,
          gap: "0.6rem",
        }}>
          {modelValues.map((v, i) => (
            <Box key={i} sx={{
              display: "flex", justifyContent: "space-between",
              alignItems: "center", gap: 1.5,
              py: 0.5, px: 1,
              borderRadius: "8px",
              backgroundColor: C.cardHeader,
              border: `1px solid ${C.borderLight}`,
            }}>
              <Typography sx={{ fontSize: "0.9rem", color: C.textSecondary, fontFamily: SANS, lineHeight: 1.3 }}>
                {v.label}
              </Typography>
              <Typography sx={{
                fontSize: "1.05rem", fontWeight: 700,
                color: C.textPrimary, fontFamily: MONO, flexShrink: 0,
              }}>
                {v.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Key Inputs + Debt/Cash */}
      <Grid container spacing={isMobile ? 1 : 1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <SectionCard title="Key Inputs" IconComp={AssessmentIcon} accent={C.navyMid}>
            {[
              { label: "Shares Outstanding",          value: Utils.format_number_long(stockValues.compProfile.sharesOutstanding) },
              { label: "Discount Rate",               value: `${stockValues.tenDCFiv.discRt}%` },
              { label: "Beta",                        value: stockValues.compProfile.beta },
              { label: "Cash Flow Growth (1–5 yr)",   value: Utils.format_percent(stockValues.tenDCFiv.growhthRate_1_5) },
              { label: "Cash Flow Growth (5–10 yr)",  value: Utils.format_percent(stockValues.tenDCFiv.growhthRate_5_10) },
              { label: "Cash Flow Growth (10–20 yr)", value: Utils.format_percent(stockValues.tenDCFiv.growhthRate_10_20) },
            ].map((row, i, arr) => (
              <MetricRow key={i} label={row.label} value={row.value}
                valueColor={C.navyMid} divider={i < arr.length - 1} />
            ))}
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <SectionCard title="Debt & Cash Position" IconComp={AccountBalanceIcon} accent="#4f46e5">
            {[
              { label: "Total Debt",          value: Utils.format_currency_long(stockValues.tenDCFiv.totalDebt),  color: C.danger },
              { label: "Debt per Share (−)",   value: Utils.format_currency_usd(stockValues.tenDCFiv.dbtPrShr),   color: C.danger },
              { label: "Cash & Investments",   value: Utils.format_currency_long(stockValues.tenDCFiv.totalCash), color: C.success },
              { label: "Cash per Share (+)",   value: Utils.format_currency_usd(stockValues.tenDCFiv.cshPrShr),   color: C.success },
            ].map((row, i, arr) => (
              <MetricRow key={i} label={row.label} value={row.value}
                valueColor={row.color} divider={i < arr.length - 1} />
            ))}

            {(() => {
              const debt  = Math.abs(Number(stockValues.tenDCFiv.dbtPrShr) || 0);
              const cash  = Math.abs(Number(stockValues.tenDCFiv.cshPrShr) || 0);
              const total = debt + cash;
              if (!total) return null;
              const dp = (debt / total) * 100;
              const cp = (cash / total) * 100;
              return (
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.6 }}>
                    <Typography sx={{ fontSize: "0.78rem", color: C.danger, fontWeight: 700, fontFamily: SANS }}>
                      Debt {dp.toFixed(0)}%
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: C.success, fontWeight: 700, fontFamily: SANS }}>
                      Cash {cp.toFixed(0)}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", height: 7, borderRadius: "4px", overflow: "hidden", backgroundColor: C.borderLight }}>
                    <Box sx={{ width: `${dp}%`, backgroundColor: C.danger, opacity: 0.65 }} />
                    <Box sx={{ width: `${cp}%`, backgroundColor: C.success, opacity: 0.65 }} />
                  </Box>
                </Box>
              );
            })()}
          </SectionCard>
        </Grid>
      </Grid>

      {/* 4 analysis cards */}
      <Grid container spacing={isMobile ? 1 : 1.5}>
        {analysisCards.map((card, i) => (
          <Grid key={i} item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
            <AnalysisCard title={card.title} accent={card.accent} data={card.data} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}